import type {
  InsurerContact,
  InsurerContactPayload,
  InsurerContacts,
  KnowledgeSearchResult,
  KnowledgeSearchStatus,
  OwuDocument,
  OwuLibraryStatus,
  OwuScanRun,
  OwuSource,
  OwuUploadDetails,
  ToolkitDocument,
  ToolkitDocumentDetails,
} from "@/types/toolkit";
import { apiClient } from "./client";

export const toolkitApi = {
  logTermination(
    reason: "art28" | "art28a" | "art31",
    source: "manual" | "client",
  ) {
    return apiClient.post<{ logged: boolean }>(
      "/api/toolkit/terminations/log",
      { reason, source },
    );
  },
  getContacts() {
    return apiClient.get<InsurerContacts[]>("/api/toolkit/contacts");
  },

  createContact(insurerId: string, payload: InsurerContactPayload) {
    return apiClient.post<InsurerContact>("/api/toolkit/contacts", {
      insurerId,
      ...payload,
    });
  },

  updateContact(id: string, payload: InsurerContactPayload) {
    return apiClient.put<InsurerContact>(
      `/api/toolkit/contacts/${id}`,
      payload,
    );
  },

  deleteContact(id: string) {
    return apiClient.delete<void>(`/api/toolkit/contacts/${id}`);
  },

  getDocuments() {
    return apiClient.get<ToolkitDocument[]>("/api/toolkit/documents");
  },

  uploadDocument(details: ToolkitDocumentDetails, file: File) {
    const formData = new FormData();
    formData.append("title", details.title);
    formData.append("category", details.category);
    formData.append("description", details.description ?? "");
    formData.append("file", file);
    return apiClient.postFormData<ToolkitDocument>(
      "/api/toolkit/documents",
      formData,
    );
  },

  updateDocument(id: string, details: ToolkitDocumentDetails) {
    return apiClient.put<ToolkitDocument>(
      `/api/toolkit/documents/${id}`,
      details,
    );
  },

  replaceDocumentFile(id: string, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.postFormData<ToolkitDocument>(
      `/api/toolkit/documents/${id}/file`,
      formData,
    );
  },

  deleteDocument(id: string) {
    return apiClient.delete<void>(`/api/toolkit/documents/${id}`);
  },

  async downloadDocument(id: string) {
    const { blob } = await apiClient.getBlob(
      `/api/toolkit/documents/${id}/file`,
    );
    return blob;
  },

  getOwuDocuments(includeArchived: boolean) {
    return apiClient.get<OwuDocument[]>(
      `/api/toolkit/owu?includeArchived=${includeArchived}`,
    );
  },

  getOwuStatus() {
    return apiClient.get<OwuLibraryStatus>("/api/toolkit/owu/status");
  },

  uploadOwu(details: OwuUploadDetails, file: File) {
    const formData = new FormData();
    formData.append("insurerId", details.insurerId);
    formData.append("product", details.product);
    formData.append("name", details.name);
    formData.append("versionLabel", details.versionLabel ?? "");
    formData.append("validFrom", details.validFrom ?? "");
    formData.append("file", file);
    return apiClient.postFormData<OwuDocument>("/api/toolkit/owu", formData);
  },

  deleteOwu(id: string) {
    return apiClient.delete<void>(`/api/toolkit/owu/${id}`);
  },

  async downloadOwu(id: string) {
    const { blob } = await apiClient.getBlob(`/api/toolkit/owu/${id}/file`);
    return blob;
  },

  startOwuScan() {
    return apiClient.post<OwuScanRun>("/api/toolkit/owu/scan", {});
  },

  getOwuSources() {
    return apiClient.get<OwuSource[]>("/api/toolkit/owu/sources");
  },

  addOwuSource(insurerId: string, domain: string, startUrl: string) {
    return apiClient.post<OwuSource>("/api/toolkit/owu/sources", {
      insurerId,
      domain,
      startUrl: startUrl || null,
    });
  },

  deleteOwuSource(id: string) {
    return apiClient.delete<void>(`/api/toolkit/owu/sources/${id}`);
  },

  getSearchStatus() {
    return apiClient.get<KnowledgeSearchStatus>("/api/toolkit/search/status");
  },

  search(question: string) {
    return apiClient.post<KnowledgeSearchResult>("/api/toolkit/search", {
      question,
    });
  },
};
